'use client';

import * as React from 'react';
import { ComponentProps } from 'react';
import { cn, TCheckBoxProps } from 'types';

type TCheckboxComponentProps = TCheckBoxProps & Pick<ComponentProps<'input'>, 'checked' | 'onChange'>;

export const CheckBox = (props: Partial<TCheckboxComponentProps>) => {
  const {
    checked,
    children,
    disabled = false,
    readonly = false,
    label,
    id,
    onChange,
    wrapperId,
    className,
    customLabelContent,
    ariaLabel,
    ...rest
  } = props;
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || readonly) return;
    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
    inputRef.current?.click();
  };

  return (
    <div
      {...rest}
      id={wrapperId}
      className={cn('flex items-start flex-col gap-1 w-fit h-fit', className)}
      onClick={handleWrapperClick}
    >
      <div className="flex items-start gap-2">
        <input
          ref={inputRef}
          role="checkbox"
          id={id}
          checked={checked}
          type={'checkbox'}
          disabled={disabled}
          aria-checked={checked}
          aria-label={ariaLabel}
          readOnly={readonly}
          onChange={onChange}
          className={cn(
            'align-middle cursor-pointer appearance-none h-[1.125rem] w-[1.125rem] border-2 border-solid border-concrete rounded-sm mt-[5px]!',
            {
              'bg-black border-2 border-solid border-black': checked,
              'border-2 border-solid border-neutralPlus pointer-events-none cursor-not-allowed': disabled,
              'border-neutralPlus bg-neutralPlus': checked && disabled,
            },
            'checked:block checked:bg-[url(/checkmark.svg)] checked:bg-no-repeat'
          )}
        />
        {children ? (
          <div className="childWrapper pt-[5px]">
            <div
              className={cn(
                'relative cursor-pointer inline-block align-middle text-base font-semibold leading-[175%] "max-sm:pl-3 text-black flex-1',
                {
                  'text-neutralPlus pointer-events-none': disabled,
                  'hover:underline': !disabled,
                  'bg-black border-2 border-black': checked,
                }
              )}
            >
              {label}
            </div>
            <div>{children}</div>
          </div>
        ) : (
          <div
            className={cn(
              'relative cursor-pointer inline-block align-middle text-base font-semibold leading-[175%] max-sm:pl-3 text-black flex-1 h-full, empty:hidden',
              {
                'text-neutralPlus pointer-events-none': disabled,
                'hover:underline': !disabled,
              }
            )}
          >
            {customLabelContent || (label ?? '')}
          </div>
        )}
      </div>
    </div>
  );
};
